import React from 'react';
import { IReview } from '@common-types';
import { Rate } from 'antd';
import { MessageOutlined } from '@ant-design/icons';

interface ReviewItemProps {
    review: IReview;
    onPhotoClick?: (photoUrl: string) => void;
}

export const ReviewItem: React.FC<ReviewItemProps> = ({ review, onPhotoClick }) => {
    const [isExpanded, setIsExpanded] = React.useState(false);
    const [isHostExpanded, setIsHostExpanded] = React.useState(false);
    const [reviewText, setReviewText] = React.useState('');
    const [hostResponseText, setHostResponseText] = React.useState('');

    React.useEffect(() => {
        // Populate text content after mount to ensure it's not in the initial HTML source
        if (review?.text) {
            setReviewText(review.text);
        }
        if (review?.ownerResponse?.text) {
            setHostResponseText(review.ownerResponse.text);
        }
    }, [review?.text, review?.ownerResponse?.text]);

    const formatDate = (dateStr: string) => {
        const date = new Date(dateStr);
        return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
    };

    const TEXT_LIMIT = 250;

    const shouldTruncate = reviewText?.length > TEXT_LIMIT;
    const displayReviewText = (shouldTruncate && !isExpanded)
        ? `${reviewText?.substring(0, TEXT_LIMIT)}...`
        : reviewText;

    const shouldTruncateHost = hostResponseText?.length > 150;
    const displayHostText = (shouldTruncateHost && !isHostExpanded)
        ? `${hostResponseText?.substring(0, 150)}...`
        : hostResponseText;

    return (
        <div className="review-item">
            <div className="review-header">
                <div className="review-stars">
                    <Rate disabled defaultValue={review.rating} />
                </div>
            </div>

            <div className="review-title">{review.title}</div>
            <div className="review-meta">
                {review.userName}, {formatDate(review.publishedDate)}
            </div>

            <div className="review-text">
                {displayReviewText}
                {shouldTruncate && (
                    <span
                        className="read-more-btn"
                        onClick={() => setIsExpanded(!isExpanded)}
                        style={{ color: '#333', fontWeight: '700', fontSize: '14px', cursor: 'pointer', marginLeft: '5px' }}
                    >
                        {isExpanded ? 'Read less' : 'Read more'}
                    </span>
                )}
            </div>

            {review.photos && review.photos.length > 0 && (
                <div className="review-item-photos">
                    {review.photos.map((photo, idx) => {
                        const bestVersion = photo.versions.find(v => v.width >= 400) || photo.versions[0];
                        return (
                            <div
                                key={idx}
                                className="photo-wrapper"
                                onClick={() => onPhotoClick?.(bestVersion.url)}
                            >
                                <img src={bestVersion.url} alt={`Review photo ${idx + 1}`} />
                            </div>
                        );
                    })}
                </div>
            )}
            {review?.ownerResponse && (
                <div className="host-response">
                    <div className="response-header">
                        <MessageOutlined className="response-icon" />
                        Response from Host, {formatDate(review?.ownerResponse?.publishedDate)}
                    </div>
                    <div className="response-text">
                        {displayHostText}
                        {shouldTruncateHost && (
                            <span
                                className="read-more"
                                onClick={() => setIsHostExpanded(!isHostExpanded)}
                                style={{ cursor: 'pointer' }}
                            >
                                {isHostExpanded ? ' Read less' : ' Read more'}
                            </span>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};
